'use client';

import { useNow, useTranslations } from 'next-intl';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { SeatLockDto } from '@cineamo/api/models/seat-lock/SeatLockDto.types';

import { cn } from '@cineamo/ui/src/helpers/cn';
import { tailwindColors } from '@cineamo/ui/src/helpers/tailwind-config-helper';

import useLocalStorage from '@cineamo/composables/src/hooks/useLocalStorage';

import Countdown, { CountdownApi } from '@cineamo/ui/src/countdown/Countdown';
import { IconSandClock16 } from '@cineamo/ui/src/icons/ic-sandClock';

import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';
import { useCartProvider } from '@cineamo/composables/src/providers/cartProvider';

import CountdownExpiredModal from './CountdownExpiredModal';
import CountdownInfoModal from './CountdownInfoModal';

const LockCountdown: React.FC = () => {
    const t = useTranslations();
    const { track } = useAnalytics();
    const lockCountdownRef = useRef<CountdownApi>(null);
    const {
        items: cartItems,
        refetchSeatLocks,
        dto,
        initEmptyCart,
        total,
        setCurrentStep: setStep
    } = useCartProvider();
    const [isCompleted, setIsCompleted] = useState(false);
    const [isCountdownInfoModalOpen, setIsCountdownInfoModalOpen] = useState(false);
    const [isCountdownExpiredModalOpen, setIsCountdownExpiredModalOpen] = useLocalStorage(
        'isCountdownExpiredModalOpen',
        'false'
    );
    const currentTime = useNow({
        updateInterval: 1000
    });

    const targetTimestamp = useMemo(() => {
        if (dto?.isPaid === true) {
            return -1;
        }
        const firstSeatLockCartItem = cartItems.find((cartItem) => cartItem._embedded?.product?.type === 'SeatLock');
        if (firstSeatLockCartItem) {
            const expireTimestamp = new Date(
                (firstSeatLockCartItem._embedded?.product as SeatLockDto).expireDatetime
            ).getTime();
            return expireTimestamp > 0 ? expireTimestamp : -1;
        }
        return -1;
    }, [dto, cartItems]);

    const lockCountdownInitialTime = useMemo(() => {
        if (dto?.isPaid === true || targetTimestamp <= 0) {
            return -1;
        }
        const calculatedTime = Math.floor((targetTimestamp - currentTime.getTime()) / 1000);
        return Math.max(-1, calculatedTime);
    }, [dto, targetTimestamp, currentTime]);

    useEffect(() => {
        if (isCompleted) {
            setIsCountdownExpiredModalOpen('true');
            refetchSeatLocks();
            initEmptyCart();
            setStep('01_seat_picking');
            track('Countdown Expired', {
                cartTotal: total.toString()
            });
            // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: reset completion flag after handling countdown expiry side-effects
            setIsCompleted(false);
        }
    }, [initEmptyCart, isCompleted, refetchSeatLocks, setStep, setIsCountdownExpiredModalOpen, total, track]);

    useEffect(() => {
        if (isCountdownInfoModalOpen) {
            track('Countdown Info Modal Opened');
        }
    }, [isCountdownInfoModalOpen, track]);

    useEffect(() => {
        const lockCountdown = lockCountdownRef.current;
        if (!lockCountdown) return;

        if (lockCountdownInitialTime > 0) {
            lockCountdown.start();
        } else {
            lockCountdown.stop();
        }
    }, [lockCountdownInitialTime]);

    const onComplete = useCallback(() => {
        setIsCompleted(true);
    }, []);

    const isHidden = lockCountdownInitialTime <= 0;

    return (
        <>
            <CountdownExpiredModal
                isOpen={isCountdownExpiredModalOpen === 'true'}
                setIsOpen={(value) => setIsCountdownExpiredModalOpen(value.toString())}
            />
            <CountdownInfoModal
                isOpen={isCountdownInfoModalOpen}
                targetTimestamp={targetTimestamp}
                setIsOpen={setIsCountdownInfoModalOpen}
            />

            <button
                className={cn('lg:mx-[16px] py-4 text-center bg-purple-pale leading-[24px] rounded-4', {
                    hidden: isHidden
                })}
                onClick={() => setIsCountdownInfoModalOpen(true)}>
                <IconSandClock16
                    className="inline mb-1 mr-6"
                    fill={tailwindColors.purple.DEFAULT}
                />
                <span className="text-body-bold-14 text-purple tracking-[.7px]">
                    {t('tickets.reserved-for-you')}:&nbsp;
                    <Countdown
                        className="inline-block w-44 text-center"
                        targetTimestamp={targetTimestamp}
                        autoStart={false}
                        onComplete={onComplete}
                        ref={lockCountdownRef}
                    />
                    &nbsp;{t('common.time.minute-short-label')}
                </span>
            </button>
        </>
    );
};

export default LockCountdown;
